'use client';

import { Button } from '@/components/button';
import { IconButton } from '@/components/iconButton';
import { SvgIcon } from '@/components/svgIcon';
import { useIsMounted } from '@/hooks/useIsMounted';
import CloseIcon from '@/public/icons/schliessen.svg';
import { Dialog } from '@base-ui/react/dialog';
import classNames from 'classnames';
import { type FC, type ReactNode, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './Popup.module.scss';

export type PopupProps = {
  open: boolean;
  onClose: () => void;

  title: string | ReactNode;
  text: ReactNode;

  ctaText?: string;
  ctaHref?: string;
  ctaTarget?: string;
  onCtaClick?: () => void;

  className?: string;
};

const Popup: FC<PopupProps> = ({ open, onClose, title, text, ctaText, ctaHref, ctaTarget, onCtaClick, className }) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const ctaButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const hasCta = !!(ctaText && (ctaHref || onCtaClick));

  if (!isMounted) {
    return null;
  }

  return (
    <Dialog.Root
      disablePointerDismissal
      modal={false}
      onOpenChange={(nextOpen) => !nextOpen && open && onClose()}
      open={open}
    >
      <Dialog.Portal>
        <Dialog.Popup
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          className={classNames(classes.popup, className)}
          initialFocus={hasCta ? ctaButtonRef : closeButtonRef}
        >
          <div className={classes.header}>
            <h3 className={classes.title} id={titleId}>
              {title}
            </h3>
            <IconButton
              ref={closeButtonRef}
              className={classes.closeButton}
              onClick={onClose}
              aria-label={t('Schließen')}
              size="small"
            >
              <SvgIcon component={CloseIcon} inheritViewBox aria-hidden={true} />
            </IconButton>
          </div>

          <div className={classes.text} id={descriptionId}>
            {text}
          </div>

          {hasCta && (
            <Button
              ref={ctaButtonRef}
              className={classes.ctaButton}
              variant="ctaFull"
              onClick={onCtaClick}
              href={ctaHref}
              target={ctaTarget}
            >
              {ctaText}
            </Button>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Popup;
